import React, { useMemo } from 'react';
import { isNaN, isFinite } from 'lodash';
import {
  IProjectColors,
  IProjectSpaceTypeCategory,
  IProjectSpaceTypeSubcategory,
} from 'spw-redux/modules/projects/types';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import SmallDonutChartDetail from 'spw-components/charts/small-donut-chart-detail';
import { PlainWorksheet } from '../../../../models/Worksheet';
import { useCategoryData } from './hooks/use-category-data';

interface IProps {
  style?: { [key: string]: number | string };
  chartStyle?: { [key: string]: number | string };
  showNamesInTable?: boolean;
  title: string;
  subtitle: string;
  projectSpaceTypeCategory: IProjectSpaceTypeCategory;
  projectColors: IProjectColors;
  worksheet: PlainWorksheet;
  isTotalProgramScenario: boolean;
}

const SpaceTypeAllocationUsfByCategoryChart: React.FunctionComponent<IProps> = ({
  style,
  chartStyle,
  showNamesInTable,
  title,
  subtitle,
  projectSpaceTypeCategory,
  projectColors,
  worksheet,
  isTotalProgramScenario,
}) => {
  const { getUnit, convertArea } = useUnitConversion();
  const { colors, titles, getData } = useCategoryData(
    projectSpaceTypeCategory,
    projectColors
  );
  const totalUsf = worksheet.workspacesUSF;

  const subcategoriesData = useMemo(() => {
    return projectSpaceTypeCategory.subcategories?.map(
      (subcategory: IProjectSpaceTypeSubcategory, index: number) => {
        const defaultSubcategory =
          subcategory.defaultSpaceTypeCategory.category;

        const subcategoryUsf =
          worksheet.subcategoriesTotals[defaultSubcategory]?.USF || 0;
        const subcategoryUsfPercent = subcategoryUsf / totalUsf;
        let subcategoryUsfPercentValue = 0;

        if (!isNaN(subcategoryUsfPercent) && isFinite(subcategoryUsfPercent)) {
          subcategoryUsfPercentValue = subcategoryUsfPercent;
        }

        return {
          quantity: convertArea(subcategoryUsf),
          percent: subcategoryUsfPercentValue,
          name: titles[index],
          color: colors[index],
        };
      }
    );
  }, [
    projectSpaceTypeCategory.subcategories,
    worksheet.subcategoriesTotals,
    titles,
    colors,
    totalUsf,
    convertArea,
  ]);

  const data = getData(subcategoriesData);

  return (
    <div style={style}>
      <SmallDonutChartDetail
        style={chartStyle}
        title={title}
        subtitle={subtitle.replace('USF', getUnit('area'))}
        total={convertArea(totalUsf)}
        isTotalProgramScenario={isTotalProgramScenario}
        spacesData={subcategoriesData}
        data={data}
        colors={colors}
        showNamesInTable={showNamesInTable}
        testingPrefix="programming_ps_spacetypealloc_bycat"
      />
    </div>
  );
};

export default SpaceTypeAllocationUsfByCategoryChart;
